import React, { useMemo } from 'react';
import OlfaktionLogo from './OlfaktionLogo';

// ─── Types ────────────────────────────────────────────────────────────────────

export interface OdorZone {
  label: string;
  min: number;
  max: number;
  color: string;
  flex: number;
}

interface OdorResultProps {
  intensity: number;
  patientId: string;
  date: number;
  zones?: OdorZone[];
}

export const DEFAULT_ODOR_ZONES: OdorZone[] = [
  { label: 'No odor', min: 0, max: 2, color: '#42A5F5', flex: 2 },
  { label: 'Low odor', min: 2, max: 4, color: '#26C6DA', flex: 2 },
  { label: 'Medium odor', min: 4, max: 8, color: '#F5A623', flex: 4 },
  { label: 'High odor', min: 8, max: 10, color: '#E91E8C', flex: 2 },
];

const BRAND_DARK = '#12102B';

// ─── Helpers ──────────────────────────────────────────────────────────────────

function getZone(intensity: number, zones: OdorZone[]): OdorZone {
  return zones.find((z) => intensity >= z.min && intensity <= z.max) ?? zones[zones.length - 1];
}

function formatDate(ts: number): string {
  return new Date(ts).toLocaleDateString('en-GB', {
    day: '2-digit',
    month: 'short',
    year: 'numeric',
  });
}

function formatTime(ts: number): string {
  return new Date(ts).toLocaleTimeString('en-GB', {
    hour: '2-digit',
    minute: '2-digit',
  });
}

// ─── Main Component ───────────────────────────────────────────────────────────

export const OdorResult: React.FC<OdorResultProps> = ({ intensity, patientId, date, zones = DEFAULT_ODOR_ZONES }) => {
  // ✅ Derive scale boundaries from zones FIRST
  const minValue = zones[0].min;
  const maxValue = zones[zones.length - 1].max;

  // ✅ Clamp using actual zone scale, not hardcoded 10
  const clamped = Math.min(maxValue, Math.max(minValue, intensity));

  const zone = useMemo(() => getZone(clamped, zones), [clamped, zones]);

  // ✅ Percentage accounts for minValue offset
  const pct = ((clamped - minValue) / (maxValue - minValue)) * 100;

  // ✅ Unique tick values derived from zone boundaries
  const ticks = [...new Set(zones.flatMap((z) => [z.min, z.max]))];

  const styles: Record<string, React.CSSProperties> = {
    card: {
      background: BRAND_DARK,
      borderRadius: 24,
      padding: '24px 20px 28px',
      width: '100%',
      maxWidth: 360,
      margin: '0 auto',
      boxSizing: 'border-box',
      fontFamily: "'Nunito Sans', 'Helvetica Neue', Arial, sans-serif",
    },
    divider: {
      border: 'none',
      borderTop: '0.5px solid rgba(255,255,255,0.1)',
      margin: '0 0 24px',
    },
    metaLabel: {
      fontSize: 10,
      color: 'rgba(255,255,255,0.35)',
      letterSpacing: '0.06em',
      textTransform: 'uppercase' as const,
      marginBottom: 2,
    },
    score: {
      fontSize: 72,
      fontWeight: 700,
      lineHeight: 1,
      color: zone.color,
      textAlign: 'center' as const,
    },
    zonePill: {
      display: 'inline-block',
      marginTop: 10,
      padding: '4px 14px',
      borderRadius: 20,
      background: `${zone.color}22`,
      border: `1px solid ${zone.color}66`,
      fontSize: 13,
      fontWeight: 600,
      color: zone.color,
    },
    track: {
      position: 'relative' as const,
      margin: '0 4px 10px',
    },
    trackBar: {
      display: 'flex',
      height: 12,
      borderRadius: 6,
      overflow: 'hidden',
    },
    marker: {
      position: 'absolute' as const,
      top: -9,
      left: `${pct}%`,
      transform: 'translateX(-50%)',
      width: 30,
      height: 30,
      borderRadius: '50%',
      background: zone.color,
      border: `3px solid ${BRAND_DARK}`,
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      fontSize: 9,
      fontWeight: 700,
      color: BRAND_DARK,
    },
  };

  return (
    <div style={styles.card}>
      <OlfaktionLogo />

      {/* Patient + date header */}
      <div
        style={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          marginBottom: 16,
        }}
      >
        <div>
          <div style={styles.metaLabel}>Patient</div>
          <div style={{ fontSize: 16, fontWeight: 600, color: '#FFFFFF' }}>{patientId}</div>
        </div>
        <div style={{ textAlign: 'right' }}>
          <div style={styles.metaLabel}>Measured</div>
          <div style={{ fontSize: 12, color: 'rgba(255,255,255,0.5)' }}>{formatDate(date)}</div>
          <div style={{ fontSize: 11, color: 'rgba(255,255,255,0.3)' }}>{formatTime(date)}</div>
        </div>
      </div>

      <hr style={styles.divider} />

      {/* Score */}
      <div style={{ textAlign: 'center', marginBottom: 28 }}>
        <div style={styles.score}>{clamped.toFixed(1)}</div>
        {/* ✅ Dynamic scale label */}
        <div style={{ fontSize: 12, color: 'rgba(255,255,255,0.35)', marginTop: 2 }}>intensity / {maxValue}</div>
        <div style={styles.zonePill}>{zone.label}</div>
      </div>

      {/* Track */}
      <div style={styles.track}>
        {/* Track bar segments — width proportional to actual value range */}
        <div style={styles.trackBar}>
          {zones.map((z) => {
            const widthPct = ((z.max - z.min) / (maxValue - minValue)) * 100;
            return (
              <div
                key={z.label}
                style={{
                  width: `${widthPct}%`,
                  flexShrink: 0,
                  background: z.color,
                  opacity: z.label === zone.label ? 0.9 : 0.45,
                  transition: 'opacity 0.2s ease',
                }}
              />
            );
          })}
        </div>

        <div style={styles.marker}>{clamped.toFixed(1)}</div>

        {/* ✅ Ticks absolutely positioned by real value percentage */}
        <div style={{ position: 'relative', height: 16, marginTop: 4 }}>
          {ticks.map((t) => {
            const tickPct = ((t - minValue) / (maxValue - minValue)) * 100;
            return (
              <span
                key={t}
                style={{
                  position: 'absolute',
                  left: `${tickPct}%`,
                  transform: 'translateX(-50%)',
                  fontSize: 9,
                  color: 'rgba(255,255,255,0.25)',
                  whiteSpace: 'nowrap',
                }}
              >
                {t}
              </span>
            );
          })}
        </div>
      </div>

      {/* Zone legend */}
      <div
        style={{
          marginTop: 20,
          display: 'flex',
          flexDirection: 'column',
          gap: 6,
        }}
      >
        {zones.map((z) => {
          const isActive = z.label === zone.label;
          return (
            <div key={z.label} style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
              <div
                style={{
                  width: 10,
                  height: 10,
                  borderRadius: '50%',
                  background: z.color,
                  opacity: isActive ? 1 : 0.35,
                  flexShrink: 0,
                }}
              />
              <span
                style={{
                  fontSize: 11,
                  color: isActive ? z.color : 'rgba(255,255,255,0.3)',
                  fontWeight: isActive ? 700 : 400,
                  transition: 'color 0.2s ease',
                }}
              >
                {z.label}{' '}
                <span style={{ opacity: 0.6 }}>
                  ({z.min}–{z.max})
                </span>
              </span>
            </div>
          );
        })}
      </div>
    </div>
  );
};

export default OdorResult;
